import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { theme } from "../../../../tailwind.config.js";
import { MightierLink } from "../../../components/MightierLink/MightierLink";
// API
import { mcsGqlClient } from "../../../utils/mcsGqlClient";
import { INTAKE_SUBMIT_MUT } from "../../../queries/intakeSubmit";
import { useIntakeFormStore } from "../intakeFormState";
// UI
import { Typography } from "../../../components/Typography/Typography";
import { IntakeFormScreenContainer } from "../IntakeFormScreenContainer";
import GridLoader from "react-spinners/GridLoader";
// util
import * as Sentry from "@sentry/react";
import { resolvePromiseAfter } from "../../../utils/sleep";
import { doesStringLookLikeDjangoGqlId } from "../../../utils/gqlUtils";
import { buildTypeformUrl, getMemberDatabasePk } from "../../../utils/intakeFormSubmitUtils";

import type { SupportedLanguage } from "../../../i18n/i18n";
// types
import type {
  SubmitIntakeFormMutation,
  IntakeSubmitInputType,
  PhiFileReferenceClaimType,
} from "../../../__generated__/mcs_backend_v1/graphql";

/**
 * some smooooooth TS to make 'status' and 'data' be in-sync :)
 */

type MutationStateNonSuccess = {
  status: "pending" | "idle" | "error";
  data: undefined;
};

type MutationStateSuccess = {
  status: "success";
  data: SubmitIntakeFormMutation;
};

type SubmitMutation = {
  mutate: () => Promise<SubmitIntakeFormMutation | undefined>;
} & (MutationStateSuccess | MutationStateNonSuccess);

const useSubmitMutation = (): SubmitMutation => {
  const [status, setStatus] = useState<MutationStateNonSuccess["status"]>("idle");
  const [data, setData] = useState<SubmitIntakeFormMutation | undefined>();
  const {
    i18n: { language: currentLanguage },
  } = useTranslation();

  const intakeFormStore = useIntakeFormStore();

  const mutate = useCallback(async () => {
    const { caregiverInfo, caregiverAddress, memberInfo, insuranceInfo, consent } = intakeFormStore;
    if (!insuranceInfo || !memberInfo || !caregiverInfo || !caregiverAddress || !consent) {
      Sentry.captureMessage("Attempted to submit intake form with invalid data");
      setStatus("error");
      return;
    }

    setStatus("pending");

    const { insuranceCardFront, insuranceCardBack } = intakeFormStore.policyHolderImageData;

    const phiFileReferencesToClaim: PhiFileReferenceClaimType[] = [];
    if (insuranceCardFront) {
      phiFileReferencesToClaim.push(insuranceCardFront);
    }
    if (insuranceCardBack) {
      phiFileReferencesToClaim.push(insuranceCardBack);
    }

    // NOTE: there can be other options (e.g. "I dont know" or "Other").
    // In these cases, we send up `null` so the backend won't assign a value to the FK
    const insuranceProviderId = doesStringLookLikeDjangoGqlId(insuranceInfo.insuranceProviderId)
      ? insuranceInfo.insuranceProviderId
      : null;

    const policyHolderData: IntakeSubmitInputType["member"]["billingProfile"]["policyHolder"] =
      insuranceInfo.isChildPolicyHolder === true
        ? null
        : {
            firstName: insuranceInfo.subscriberFirstName,
            lastName: insuranceInfo.subscriberLastName,
            dateOfBirth: insuranceInfo.subscriberDOB,
            externalSubscriberNumber: insuranceInfo.subscriberNumber,
            groupNumber: insuranceInfo.groupNumber?.trim() || undefined,
          };

    const mutationInput: IntakeSubmitInputType = {
      member: {
        billingProfile: {
          externalMemberId: insuranceInfo.memberId,
          insuranceProvider: insuranceProviderId,
          // maybe null!
          policyHolder: policyHolderData,
          // maybe empty array!
          phiFileReferencesToClaim,
        },
        firstName: memberInfo.firstName,
        dateOfBirth: memberInfo.dateOfBirth,
        lastName: memberInfo.lastName,
        gender: memberInfo.gender,
        ethnicity: memberInfo.ethnicity,
        race: memberInfo.race,
        isHomeInternetReliable: memberInfo.haveInternet === "YES",
        caregiverRelationshipToMember: caregiverInfo.relationshipToChild,
        caregiver: {
          addressAdd: {
            address1: caregiverAddress.address1,
            address2: caregiverAddress.address2 ?? "",
            city: caregiverAddress.city,
            provinceCode: caregiverAddress.provinceCode,
            countryCode: "US",
            zipcode: caregiverAddress.zipCode,
            isCurrent: true,
          },
          email: caregiverInfo.email,
          firstName: caregiverInfo.firstName,
          lastName: caregiverInfo.lastName,
          phoneNumber: caregiverInfo.phoneNumber,
          locale: currentLanguage,
          optedInMarketingEmail: caregiverInfo.emailMarketingOptIn ?? false,
          optedInSms: caregiverInfo.smsMarketingOptIn ?? false,
        },
        consentToTreat: {
          disclosureFileName: consent.disclosureFileName,
          disclosureMd5: consent.disclosureMd5,
          methodOfSignature: consent.methodOfSignature,
          signatureRecordedAt: consent.signatureRecordedAt,
        },
      },
      additionalAnalyticsValues: [
        {
          key: "typeformUrl",
          value: buildTypeformUrl({
            memberId: null, // At this point we don't have memberId yet so backend will need to add it
            currentLanguage: currentLanguage as SupportedLanguage,
            consentToTreat: !!consent,
            caregiverInfo,
            caregiverAddress,
            memberInfo,
            insuranceProviderSlug: insuranceInfo.insuranceProviderSlug,
          }),
        },
      ],
    };

    try {
      // minimum loading time - prevent UI flicker
      const resp = await resolvePromiseAfter(500, mcsGqlClient.request(INTAKE_SUBMIT_MUT, { input: mutationInput }));
      setData(resp);
      return resp;
    } catch (e) {
      Sentry.captureMessage("Intake submit mutation failed", { extra: { err: e } });
      setStatus("error");
    }
  }, [intakeFormStore, currentLanguage]);

  if (!data) {
    return { status, mutate, data: undefined };
  }

  return { status: "success", mutate, data };
};

type AfterSuccessfulSubmit = (memberId: string | null | undefined) => void;

const useAfterSubmitActions = (): AfterSuccessfulSubmit => {
  const intakeFormStore = useIntakeFormStore();
  const {
    i18n: { language: currentLanguage },
  } = useTranslation();

  const {
    consent,
    caregiverInfo,
    caregiverAddress,
    memberInfo,
    insuranceInfo,
    reset: clearFormValues,
  } = intakeFormStore;

  return useCallback<AfterSuccessfulSubmit>(
    (memberId) => {
      if (!consent || !insuranceInfo || !memberInfo || !caregiverInfo || !caregiverAddress) {
        Sentry.captureMessage("Attempted to submit intake form with invalid data");
        return;
      }

      if (!memberId) {
        // NOTE: shouldn't happen, but TS says it _can_ (due to backend schema being custom created, )
        Sentry.captureMessage("Unable to parse memberId to include in typeform hidden fields", { extra: { memberId } });
        return;
      }

      const memberDatabasePk = getMemberDatabasePk(memberId);

      const entireTypeformUrl = buildTypeformUrl({
        memberId: memberDatabasePk,
        currentLanguage: currentLanguage as SupportedLanguage,
        consentToTreat: !!consent,
        caregiverInfo,
        caregiverAddress,
        memberInfo,
        insuranceProviderSlug: insuranceInfo.insuranceProviderSlug,
      });

      // clear data from storage
      clearFormValues();
      // then redirect
      if (entireTypeformUrl) {
        window.location.href = entireTypeformUrl;
      } else {
        Sentry.captureMessage("Typeform URL is null, cannot redirect");
      }
    },
    [consent, caregiverInfo, caregiverAddress, memberInfo, insuranceInfo, clearFormValues, currentLanguage],
  );
};

export const IntakeFormSubmitScreen = React.memo(() => {
  const { mutate, status, data } = useSubmitMutation();
  const { t } = useTranslation();
  const afterSubmit = useAfterSubmitActions();

  const hasRunAlreadyRef = useRef(false);

  useEffect(() => {
    if (status === "idle" && !hasRunAlreadyRef.current) {
      hasRunAlreadyRef.current = true;
      mutate();
    }
  }, [mutate, status]);

  useEffect(() => {
    if (data) {
      console.log("SUBMITTED, REDIRECT TO TYPEFORM");
      afterSubmit(data.intakeFormSubmit?.graphqlMemberId);
    }
  }, [data, afterSubmit]);

  return (
    <IntakeFormScreenContainer title="">
      <div className="min-h-96">
        <div className="mt-36">
          {status === "pending" ? (
            <div className="flex items-center justify-center">
              <GridLoader color={theme.colors.blueLight} />
            </div>
          ) : null}
        </div>
        <div>
          {status === "error" ? (
            <>
              <Typography variant="h3" className="text-red">
                {t("intakeFormSubmitPage.error1")}
              </Typography>
              <Typography>
                {t("intakeFormSubmitPage.error2a")}{" "}
                <MightierLink to="/intake/2">{t("intakeFormSubmitPage.error2b")}</MightierLink>
              </Typography>
            </>
          ) : null}
        </div>
      </div>
    </IntakeFormScreenContainer>
  );
});

IntakeFormSubmitScreen.displayName = "IntakeFormSubmitScreen";
